/* eslint-disable import/order */
import {
    CheckoutsActionTypes,
    DeleteCheckoutAction,
    GetAllCheckoutsStartAction,
    LoadPreviewTemplateAction,
    UpdateCheckoutStatusStartAction,
    UrlSearchParams,
} from '@/store/checkouts/types';
import {
    DeleteCheckoutError,
    DeleteCheckoutSuccess,
    GetAllCheckoutsError,
    GetAllCheckoutsStart,
    GetAllCheckoutsSuccess,
    LoadPreviewTemplateError,
    LoadPreviewTemplateSuccess,
    PickCheckouts,
    UpdateCheckoutStatusError,
    UpdateCheckoutStatusSuccess,
} from '@/store/checkouts/actions';
import { call, fork, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { deleteCheckouts, loadCheckout, loadCheckouts, updateCheckoutStatus } from '@/services/ElasticApi';
import { errorToast } from '@/sagas/commonGenerators/errorToast';
import { LandingCheckoutStatusEnum } from '@dev/ee-api/ee-api-models';
import { calculateActivePage, calculateOffset } from '@/services/tools/handleUrlParams';
import i18n from '@/../i18n';
import { infoToast } from './commonGenerators/infoToast';
import { push } from 'redux-first-history';
import { stringify } from 'query-string';

function* fetchTemplatesList(action: GetAllCheckoutsStartAction): any {
    const { payload } = action;
    const offset = calculateOffset(payload.page, payload.limit);

    const params = {
        offset,
        limit: payload.limit,
        search: payload.search,
        statusList: payload.statusList,
        page: payload.page,
        from: payload.from,
        to: payload.to,
        scope: payload.scope
    };

    const activePage = calculateActivePage(offset, payload.limit);

    try {
        const list = yield call(loadCheckouts, params);

        const urlQueryParams: UrlSearchParams = {
            page: activePage,
            perPage: payload.limit,
            ...(payload.statusList && { statuses: payload.statusList }),
            ...(payload.search && { search: payload.search }),
            ...(payload.from && { from: payload.from }),
            ...(payload.to && { to: payload.to }),
        };
        const counts = {
            [LandingCheckoutStatusEnum.Published]: Number(list.headers['x-total-published-checkouts']),
            [LandingCheckoutStatusEnum.Unpublished]: Number(list.headers['x-total-unpublished-checkouts']),
            [LandingCheckoutStatusEnum.Draft]: Number(list.headers['x-total-draft-checkouts'])
        }

        const checkoutTabCount = payload.statusList.reduce((sum, status) => sum + (counts[status] || 0), 0);
        if (!payload.isUrlUpdateDisabled) {
            yield put(push({ search: stringify(urlQueryParams as any) }));
        }
        yield put(GetAllCheckoutsSuccess(list.data, checkoutTabCount, counts[LandingCheckoutStatusEnum.Published], counts[LandingCheckoutStatusEnum.Unpublished]));
    } catch (e) {
        yield call(errorToast, e);
        yield put(GetAllCheckoutsError());
    }
}

function* previewCheckout(action: LoadPreviewTemplateAction): any {
    const { payload } = action;

    try {
        const checkout = yield call(loadCheckout, payload.name);
        yield put(LoadPreviewTemplateSuccess(checkout));
    } catch (e) {
        yield call(errorToast, e);
        yield put(LoadPreviewTemplateError());
    }
}

function* updateCheckoutStatusByName(action: UpdateCheckoutStatusStartAction): any {
    const { payload } = action;
    try {
        const checkoutStatus = yield call(updateCheckoutStatus, payload.name, payload.status);

        yield put(UpdateCheckoutStatusSuccess(checkoutStatus, payload.name));
        
    } catch (e) {
        yield call(errorToast, e);
        yield put(UpdateCheckoutStatusError(payload.name));
    }
}

function* fetchDeleteCheckouts(action: DeleteCheckoutAction): any {
    const { payload } = action;

    try {
        const offset = calculateOffset(payload.UrlParams.page, payload.UrlParams.limit);
        const params = {
            offset,
            limit: payload.UrlParams.limit,
            search: payload.UrlParams.search,
            statusList: payload.UrlParams.statusList,
            page: payload.UrlParams.page,
        };
        yield call(deleteCheckouts, payload.Names);
        yield put(DeleteCheckoutSuccess());
        yield put(GetAllCheckoutsStart(params));
        yield put(PickCheckouts([]));
        yield call(infoToast, i18n.t('checkoutsdeleted', { count: payload.Names.length }));
    } catch (e) {
        yield call(errorToast, e);
        yield put(DeleteCheckoutError());
    }
}

function* watchFetchTemplateList(): any {
    yield takeLatest(CheckoutsActionTypes.GET_ALL_CHECKOUTS_START, fetchTemplatesList);
}
function* watchPreviewCheckout(): any {
    yield takeLatest(CheckoutsActionTypes.LOAD_PREVIEW_TEMPLATE, previewCheckout);
}
function* watchUpdateCheckoutStatus(): any {
    yield takeEvery(CheckoutsActionTypes.UPDATE_CHECKOUT_STATUS, updateCheckoutStatusByName);
}
function* watchDeleteCheckouts(): any {
    yield takeLatest(CheckoutsActionTypes.DELETE_CHECKOUTS, fetchDeleteCheckouts);
}

const checkoutsListSagas = [
    fork(watchFetchTemplateList),
    fork(watchDeleteCheckouts),
    fork(watchPreviewCheckout),
    fork(watchUpdateCheckoutStatus),
];

export default checkoutsListSagas;
